import React, { useState } from 'react';
import CustomTypography from './CustomTypography';
import { useFetchContent } from './Actions/FetchContent';
import axios from 'axios';



const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Skeleton for the CMS heading — same height as one line of p3 text
const LabelSkeleton = () => (
  <div
    aria-hidden="true"
    style={{
      height: '1.5rem',
      width: '75%',
      borderRadius: 4,
      background: 'rgba(255,255,255,0.25)',
      marginBottom: 4,
    }}
  />
);

const MailchimpForm = () => {
  const shortNames = '909,910,911';
  const { apiData } = useFetchContent(shortNames);

  const [email,   setEmail]   = useState('');
  const [status,  setStatus]  = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const res = await axios.post(`${API_BASE_URL}/api/v1/newsletter/subscribe`, {
        email,
        source: 'footer',
      });
      setStatus('success');
      setMessage(res.data.message || 'Thank you for subscribing!');
      setEmail('');
    } catch (err) {
      setStatus('error');
      if (err.response?.status === 409) {
        setMessage('This email is already subscribed.');
      } else {
        setMessage(err.response?.data?.message || 'Something went wrong. Please try again.');
      }
    }
  };

  return (
    /*
      Outer wrapper: reserves the full height of the green card BEFORE the
      CMS label loads. This prevents the footer's top row from growing when
      apiData[0] arrives. 180px matches the green card's natural height —
      measure it in DevTools and adjust if needed.
    */
    <div style={{ minHeight: 180 }}>

      {/*
        Green card: explicit minHeight so the card box is stable.
        If the label is 1 line, the card is ~180px.
        If apiData[0] is missing (slow API), the skeleton fills the same space.
      */}
      <div
        className="bg-topGreen p-6 rounded-[15px] lg:mt-0 mt-5"
        style={{ minHeight: 180 }}
      >
        {/* CMS label — skeleton while loading, real text after */}
        {apiData[0]?.content
          ? (
              <CustomTypography
                as="p"
                variant="p3"
                color="text-white"
                weightFont="font-normal"
                html={apiData[0].content}
              />
            )
          : <LabelSkeleton />
        }

        <div>
          {status === 'success' ? (

            // ── Success state ──────────────────────────────────────────────
            <div style={{ paddingTop: 20 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 8,
                padding: '12px 16px',
              }}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#B3F056"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>
                  {message}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setStatus('idle')}
                style={{
                  marginTop: 12,
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 12,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Subscribe another email
              </button>
            </div>

          ) : (

            // ── Form ──────────────────────────────────────────────────────
            <form
              onSubmit={handleSubmit}
              className="validate flex flex-col lg:flex-col md:flex-row flex-col gap-5 pt-5"
              noValidate
            >
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (status === 'error') setStatus('idle');
                  }}
                  className="w-full mt-5 border-b border-white outline-none bg-transparent text-white placeholder-white"
                  placeholder="Enter your email"
                  required
                  disabled={status === 'loading'}
                />
                {status === 'error' && message && (
                  <p style={{ color: '#fca5a5', fontSize: 12, marginTop: 6 }}>
                    {message}
                  </p>
                )}
              </div>

              <div className="flex lg:justify-start items-center pt-5">
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="bg-topYellow font-manrope text-customBlack py-3 px-6 rounded-full font-semibold text-[14px] leading-[20px] hover:bg-white transition-colors hover:text-customBlack"
                  style={{
                    opacity: status === 'loading' ? 0.7 : 1,
                    cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                  }}
                >
                  {status === 'loading' ? 'Subscribing…' : 'Subscribe'}
                </button>
              </div>
            </form>

          )}
        </div>
      </div>
    </div>
  );
};

export default MailchimpForm;